import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { AlertController, ToastController } from '@ionic/angular';
import { BerService } from '../ber.service';
import { BER_OPCIOK, honapNev } from '../ber.opciok';
import { adatTabla, datum, dokumentumNyomtat, esc, ft } from '../adatszolg/dokumentum-nyomtatas';

// BER listák: havi bérlista, éves összesítő, átutalási lista. A bérfeladás a NAPLO-ba csak a Bér adatszolgáltatás › Bérfeladás
// oldalon (előnézettel, kettősnél főkönyvi számokkal) – a Bérlisták gyors-feladás gombja megszűnt (user, 2026-11-05).
@Component({
  selector: 'app-ber-listak',
  templateUrl: './listak.component.html',
  styleUrls: ['../../naplo/naplo-shared.scss', '../ber-shared.scss'],
})
export class ListakComponent implements OnInit, OnDestroy {
  nezet: 'berlista' | 'osszesito' | 'atutalas' = 'berlista';
  berlista: any = { sorok: [], osszesen: {}, kozterhek: { tetelek: [] } };
  osszesito: any[] = [];
  atutalas: any = { sorok: [], osszeg: 0 };
  evek: number[] = [];
  honapok = BER_OPCIOK.honapok;
  honapNev = honapNev;

  constructor(private ber: BerService, private toast: ToastController, private alert: AlertController,
              private route: ActivatedRoute, private router: Router) {}
  private qSub?: Subscription;
  /** A nézetet a főmenü választja (`?nezet=`), a fülsor ugyanezt állítja. */
  fulek = [{ k: 'berlista', c: 'Havi bérlista' }, { k: 'osszesito', c: 'Éves összesítő' }, { k: 'atutalas', c: 'Átutalás' }];

  async ngOnInit() {
    await this.ber.init();
    const ev = new Date().getFullYear();
    this.evek = [ev + 1, ev, ev - 1, ev - 2, ev - 3];
    this.qSub = this.route.queryParamMap.subscribe(async (q) => {
      const n = q.get('nezet');
      this.nezet = (n === 'osszesito' || n === 'atutalas') ? n : 'berlista';
      await this.betolt();
    });
  }
  ngOnDestroy() { this.qSub?.unsubscribe(); }

  get aktiv() { return this.ber.aktiv; }
  async evValt(ev: number) { await this.ber.setAktiv({ ev }); await this.betolt(); }
  async hoValt(ho: number) { await this.ber.setAktiv({ ho }); await this.betolt(); }
  nezetValt(n: any) { this.router.navigate([], { relativeTo: this.route, queryParams: { nezet: n } }); }

  async betolt() {
    if (!this.aktiv.ugyfelId) return;
    try {
      if (this.nezet === 'berlista') this.berlista = await this.ber.get('/berlista/' + this.aktiv.ugyfelId + this.ber.evhoq());
      else if (this.nezet === 'osszesito') this.osszesito = await this.ber.get('/osszesito/' + this.aktiv.ugyfelId + this.ber.evq());
      else this.atutalas = await this.ber.get('/atutalas/' + this.aktiv.ugyfelId + this.ber.evhoq());
    } catch { this.hiba('Nem sikerült betölteni.'); }
  }

  /* ---------- NYOMTATÁS (az asztali programban PDF-előnézettel) ---------- */
  private get idoszak(): string { return `${this.aktiv.ev}. ${honapNev(this.aktiv.ho).toLowerCase()}`; }
  private fejlec(cim: string): string {
    const u = this.berlista.ugyfel || {};
    const cegCim = [u.irsz, u.telepules, u.cim].filter(Boolean).join(' ');
    return `<div class="fej"><div><h1>${esc(cim)}</h1><div class="alcim">${esc(this.idoszak)}</div></div>
      <div class="jobb"><b>${esc(u.nev || this.aktiv.ugyfelNev || '')}</b>${u.adoszam ? '<br>Adószám: ' + esc(u.adoszam) : ''}${cegCim ? '<br>' + esc(cegCim) : ''}</div></div>`;
  }
  private nevCella(r: any): string {
    return r.alkalmazott_nev + (r.tipus === 'hokozi' ? ` (${r.megnevezes || 'hóközi'}${r.kifizetes ? ', ' + datum(r.kifizetes) : ''})` : '');
  }
  /** A közterhek utalási táblája (nyomtatáshoz). */
  private kozteherTabla(): string {
    const k = this.berlista.kozterhek || { tetelek: [] };
    const sorok = (k.tetelek || []).map((t: any) => [t.megnevezes, t.kedvezmenyezett || '', t.szamla ? `${t.szamla}${t.adonem ? ' (adónem ' + t.adonem + ')' : ''}` : (t.megjegyzes || ''),
      t.esedekes ? datum(t.esedekes) : '', ft(t.osszeg)]);
    return `<h2>Fizetendő közterhek és utalások</h2>${adatTabla(['Mit', 'Kinek', 'Számlaszám', 'Esedékes', 'Összeg (Ft)'], sorok, [4],
      ['NAV-nak összesen', '', '', '', ft(k.nav_osszesen)])}
      <div class="megj">A NAV-ra utalt összegek a dolgozónként forintra kerekített összegek összegei (egyeznek a 08-as bevallás adónemenkénti
      összesítőjével). A közleménybe az adózó adószámát kell írni. Számlaszámok: NAV adóztatási számlaszámok, 2026. 11. 01-jétől.</div>
      ${(k.tetelek || []).filter((t: any) => t.csoport === 'nav' && t.megjegyzes).map((t: any) => `<div class="megj">${esc(t.megjegyzes)}</div>`).join('')}`;
  }
  nyomtatBerlista() {
    const o = this.berlista.osszesen || {};
    const kiva = this.aktiv.kivas ? 'KIVA' : 'Szocho';
    const sorok = (this.berlista.sorok || []).map((r: any) => [this.nevCella(r), r.adoazonosito || '', ft(r.brutto), ft(r.szja), ft(r.tb_jarulek),
      ft(r.ekho), ft((r.letiltas || 0) + (r.egyeb_levonas || 0)), ft(r.netto), ft(r.szocho), ft(r.koltseg)]);
    const lap = this.fejlec('Havi bérlista') + adatTabla(['Munkavállaló', 'Adóazonosító', 'Bruttó', 'SZJA', 'TB-járulék', 'EKHO', 'Letiltás, egyéb', 'Nettó', kiva, 'Összköltség'],
      sorok, [2, 3, 4, 5, 6, 7, 8, 9],
      ['Összesen', `${sorok.length} sor`, ft(o.brutto), ft(o.szja), ft(o.tb_jarulek), ft(o.ekho), ft((o.letiltas || 0) + (o.egyeb_levonas || 0)), ft(o.netto), ft(o.szocho), ft(o.koltseg)])
      + this.kozteherTabla();
    dokumentumNyomtat(`Bérlista ${this.idoszak}`, [lap], 'Cronomit · Bérszámfejtés', true);
  }
  /** Átvételi lista: a munkavállaló aláírásával igazolja a bér (nettó) átvételét – az adatokkal és az összegekkel. */
  nyomtatAtveteli() {
    const o = this.berlista.osszesen || {};
    const td = (v: any, num = false) => `<td class="${num ? 'num' : ''}">${v}</td>`;
    const levon = (r: any) => (r.ekho || 0) + (r.letiltas || 0) + (r.egyeb_levonas || 0);
    const tr = (this.berlista.sorok || []).map((r: any) => `<tr>${td(`<b>${esc(this.nevCella(r))}</b><br><span class="kicsi">Adóazonosító: ${esc(r.adoazonosito || '—')}${r.taj ? ' · TAJ: ' + esc(r.taj) : ''}</span>`)}
      ${td(ft(r.brutto), true)}${td(ft(r.szja), true)}${td(ft(r.tb_jarulek), true)}${td(ft(levon(r)), true)}${td(`<b>${ft(r.netto)}</b>`, true)}
      ${td(`<span class="kicsi">${r.bankszamla ? 'átutalás<br>' + esc(r.bankszamla) : 'készpénz'}</span>`)}<td></td><td class="alairo"></td></tr>`).join('');
    const tabla = `<table class="racs atveteli"><thead><tr><th>Munkavállaló</th><th class="num">Bruttó</th><th class="num">SZJA</th><th class="num">TB-járulék</th>
      <th class="num">Egyéb levonás</th><th class="num">Nettó (Ft)</th><th>Kifizetés módja</th><th>Átvétel dátuma</th><th>Aláírás</th></tr></thead>
      <tbody>${tr || '<tr><td colspan="9" class="ures">Nincs számfejtés ebben a hónapban.</td></tr>'}
      <tr class="osszesen"><td>Összesen</td><td class="num">${ft(o.brutto)}</td><td class="num">${ft(o.szja)}</td><td class="num">${ft(o.tb_jarulek)}</td>
      <td class="num">${ft(levon(o))}</td><td class="num">${ft(o.netto)}</td><td></td><td></td><td></td></tr></tbody></table>
      <div class="megj">Egyéb levonás: EKHO, letiltás, pénztártagdíj és más levonás. A tételes bontás a bérjegyzéken (Nyomtatványok → Bérjegyzék).</div>`;
    const lap = this.fejlec('Bérjegyzék – átvételi lista')
      + `<div class="megj">Aláírásommal igazolom, hogy a(z) ${esc(this.idoszak)} havi munkabéremet a soromban szereplő nettó összegben, a bérjegyzékkel együtt átvettem.</div>`
      + tabla + `<div class="alairas"><div>Kelt: ${esc(new Date().toLocaleDateString('hu-HU'))}</div>
      <div class="vonal"><span>kifizető (munkáltató) aláírása</span></div></div>`;
    dokumentumNyomtat(`Átvételi lista ${this.idoszak}`, [lap], 'Cronomit · Bérszámfejtés', true);
  }

  async masol(szoveg: string) {
    try { await navigator.clipboard.writeText(szoveg.replace(/-/g, '')); this.ok('Számlaszám a vágólapon.'); } catch { this.hiba('A másolás nem sikerült.'); }
  }

  // CSV-export (átutalási lista)
  atutalasExport() {
    const sorok = this.atutalas.sorok || [];
    const csv = ['Név;Bankszámlaszám;Nettó'].concat(sorok.map((r: any) => `${r.alkalmazott_nev};${r.bankszamla || ''};${Math.round(r.netto)}`)).join('\n');
    const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `atutalas_${this.aktiv.ev}_${this.aktiv.ho}.csv`;
    a.click();
  }

  honapokKitoltve(alkId: string): any[] {
    return this.osszesito.filter((o) => o.alk_id === alkId);
  }
  /*  Az éves összesítő dolgozónként, EGYSZER összegezve (2026-11-05): eddig a getter minden változásészleléskor új
   *  listát adott, és az alkEves() cellánként végigszűrte a teljes összesítőt (dolgozók × oszlopok × sorok). */
  private evesTar: { o: any[]; lista: any[]; ossz: Map<string, Record<string, number>> } | null = null;
  private evesKesz() {
    if (this.evesTar && this.evesTar.o === this.osszesito) return this.evesTar;
    const nevek = new Map<string, string>(); const ossz = new Map<string, Record<string, number>>();
    for (const o of this.osszesito || []) {
      nevek.set(o.alk_id, o.alkalmazott_nev);
      const m = ossz.get(o.alk_id) || {};
      for (const [k, v] of Object.entries(o)) if (typeof v === 'number' || (typeof v === 'string' && v !== '' && !isNaN(Number(v)))) m[k] = (m[k] || 0) + (Number(v) || 0);
      ossz.set(o.alk_id, m);
    }
    this.evesTar = { o: this.osszesito, lista: Array.from(nevek.entries()).map(([id, nev]) => ({ id, nev })), ossz };
    return this.evesTar;
  }
  get alkalmazottakOsszesito(): any[] { return this.evesKesz().lista; }
  alkEves(alkId: string, mezo: string): number { return this.evesKesz().ossz.get(alkId)?.[mezo] || 0; }

  private async ok(m: string) { (await this.toast.create({ message: m, duration: 2200, color: 'success' })).present(); }
  private async hiba(m: string) { (await this.toast.create({ message: m, duration: 2800, color: 'danger' })).present(); }
}
